import { FloatingFormItem, Group, InputDecimal } from '@/components/_common';
import { Button, Col, Form, Image, Modal, Row, Table } from 'antd';
import PecaAiDictionarySelect, {
  PecaAiProductOption,
} from '../PecaAiDictionarySelect';
import { usePecaAiContext } from '../../PecaAiProvider';
import { usePecaAiSearchProduct } from '@/core/domain/pecaAi/pecaAi.hooks';
import { useCallback, useState } from 'react';
import { PecaAiSearchProductPossibilities } from '@/core/domain/pecaAi/pecaAi.types';
import { useWorkorderDetailContext } from '../../../WorkorderDetailProvider';
import { cepMask } from '@/utils/mask/commonMasks';
import { applyMask } from '@/utils/mask';
import {
  PECAAI_BASKET_DETAIL,
  usePecaAiAddProductInBasket,
} from '@/core/domain/pecaAi/basket/pecaAiBasket.hooks';
import { useWorkorderComplementSyncWithPecaAi } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.hooks';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import { WORKORDER_ITEMS_QUERY_KEY } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.hooks';
import { Check } from 'react-feather';
import formatterPrice from '@/utils/number/formatterPrice';
import { decimalToTime } from '@/utils/functions/decimalToTime';

interface FormValues {
  amount: number;
  description: string;
}

export default function PecaAiAddProductForm() {
  const [form] = Form.useForm<FormValues>();

  const amount = Form.useWatch('amount', form);

  const { basketId, cnpj } = usePecaAiContext();

  const {
    workorder: { data: workorder },
    complement: { data: complement },
    company: { data: company },
  } = useWorkorderDetailContext();
  const { addProduct, postalCode, licensePlate } = usePecaAiContext();

  const searchProduct = usePecaAiSearchProduct();

  const [possibilities, setPossibilities] = useState<
    PecaAiSearchProductPossibilities[]
  >([]);

  const addProductInBasket = usePecaAiAddProductInBasket();
  const syncWithPecaAi = useWorkorderComplementSyncWithPecaAi();

  const queryChanel = useQueryChanel();

  const isLoading =
    searchProduct.isLoading ||
    addProductInBasket.isLoading ||
    syncWithPecaAi.isLoading;

  const onProductSelect = useCallback(
    async (option: PecaAiProductOption) => {
      if (option?.label) {
        const result = await searchProduct.mutateAsync({
          plate: licensePlate as string,
          postalCode: postalCode as string,
          products: [{ amount, description: option.label }],
        });

        if (result?.products?.length > 0) {
          const product = result.products[0];
          const resultPossibilities = product?.possibilities || [];

          if (resultPossibilities.length > 1) {
            setPossibilities(
              resultPossibilities.map((possibility) => ({
                ...possibility,
                description: (
                  possibility?.description || product.description
                ).toLocaleUpperCase(),
              })),
            );
          } else if (resultPossibilities.length === 1) {
            const possibility = resultPossibilities[0];
            handleAddProduct({
              ...possibility,
              description: (
                possibility?.description || product.description
              ).toLocaleUpperCase(),
            });
          }

          if (resultPossibilities.length) {
            return form.resetFields();
          }
        }

        Modal.confirm({
          title: 'Produto não encontrado',
          content: `Não encontramos o produto "${
            option.label
          }", para o veículo "${licensePlate}", na região ${
            company?.companyAddress.district
          } - ${applyMask(company?.companyAddress.zip, cepMask)}`,
          okButtonProps: { style: { display: 'none' } },
          cancelText: 'Fechar',
        });
      }
    },
    [amount, cnpj, basketId, workorder, complement],
  );

  const handleAddProduct = async (
    possibility: PecaAiSearchProductPossibilities,
  ) => {
    if (basketId && cnpj && workorder?.id && complement?.id) {
      await addProductInBasket.mutateAsync({
        amount: possibility.amount,
        productId: possibility.id,
        basketId,
        document: cnpj,
      });
      await syncWithPecaAi.mutateAsync({
        workOrderId: workorder.id,
        complementId: complement.id,
      });
      queryChanel({
        invalidateQueries: [
          [WORKORDER_ITEMS_QUERY_KEY],
          [PECAAI_BASKET_DETAIL],
        ],
      });
    } else {
      addProduct({
        amount: possibility.amount,
        description: possibility.description,
        brand: possibility.brand,
        inBasket: false,
        partNumber: possibility.partNumber,
        productId: possibility.id,
        totalPrice: possibility.productTotalPrice,
        unitPrice: possibility.productUnitPrice,
        deadlineHours: possibility.deadlineHours,
        image: possibility.image,
        observation:
          possibility.observation.length >= 1
            ? possibility.observation[0].description
            : undefined,
      });
    }
  };

  return (
    <>
      <Form form={form} initialValues={{ amount: 1, description: null }}>
        <strong style={{ fontSize: '1.1rem' }}>Adicionar produto</strong>
        <Row gutter={[16, 16]}>
          <Col>
            <FloatingFormItem name="amount" label="Quantidade">
              <InputDecimal min={0} disabled={isLoading} />
            </FloatingFormItem>
          </Col>

          <Col flex={1}>
            <FloatingFormItem name="description" label="Descrição do produto">
              <PecaAiDictionarySelect
                value={null}
                onProductSelect={onProductSelect}
                disabled={isLoading}
              />
            </FloatingFormItem>
          </Col>
        </Row>
      </Form>

      <Modal
        open={!!possibilities.length}
        onCancel={() => setPossibilities([])}
        title="Selecione uma das variações do produto"
        width={800}
        footer={null}
      >
        <Table
          dataSource={possibilities}
          scroll={{ x: true }}
          columns={[
            {
              title: 'Produto',
              render: (_, item) => (
                <>
                  <Group gap={10}>
                    <Image
                      src={item.image}
                      preview={false}
                      width={40}
                      height={40}
                      style={{ flexShrink: 0 }}
                    />
                    <strong>{item.description}</strong>
                  </Group>

                  {!!item.observation.length && (
                    <p style={{ color: 'orange', marginTop: 16 }}>
                      {item.observation[0].description}
                    </p>
                  )}
                </>
              ),
            },
            { title: 'Cód. peça', dataIndex: 'partNumber' },
            { title: 'Marca', dataIndex: 'brand' },
            {
              title: 'Preço',
              render: (_, item) => formatterPrice(item.productUnitPrice),
            },
            {
              title: 'Frete',
              render: (_, item) => formatterPrice(item.shippingPrice),
            },
            {
              title: 'Prev. de entrega',
              render: (_, item) => decimalToTime(item.deadlineHours),
            },
            {
              align: 'right',
              render: (_, item) => (
                <Button
                  shape="circle"
                  onClick={() => {
                    handleAddProduct(item);
                    setPossibilities([]);
                  }}
                  type="primary"
                  icon={<Check size={16} />}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                />
              ),
            },
          ]}
          pagination={false}
        />
      </Modal>
    </>
  );
}
